import { z } from 'zod';

import { imageUrlSchema } from './image';

export const serviceStatusSchema = z.enum(['draft', 'active', 'archived']);
export const servicePricingModeSchema = z.enum(['fixed', 'configured']);
export const serviceBookingModeSchema = z.enum([
  'appointment',
  'quote_request',
  'both',
]);
export const serviceBookingStatusSchema = z.enum([
  'pending',
  'confirmed',
  'completed',
  'cancelled',
]);
export const serviceIconSchema = z.enum([
  'mechanic',
  'antifouling',
  'maintenance',
  'electricity',
  'cleaning',
  'inspection',
  'fuel',
  'water',
  'wrench',
]);
export const serviceSlugSchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9-]{0,62}$/);

export const serviceCategoryInputSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().max(2000).optional().nullable(),
});

export const serviceDetailsSchema = z
  .object({
    howItWorks: z.array(z.string().trim().min(1).max(500)).max(20),
    includes: z.array(z.string().trim().min(1).max(500)).max(30),
    notes: z.array(z.string().trim().min(1).max(500)).max(20),
    afterRequest: z.string().trim().max(2000).nullable(),
  })
  .strict();

export const emptyServiceDetailsInput = {
  howItWorks: [] as string[],
  includes: [] as string[],
  notes: [] as string[],
  afterRequest: null as string | null,
};

export const serviceInputSchema = z.object({
  name: z.string().trim().min(1).max(255),
  slug: serviceSlugSchema,
  icon: serviceIconSchema.default('wrench'),
  imageUrl: imageUrlSchema.optional().nullable(),
  description: z.string().trim().max(1000).optional().nullable(),
  details: serviceDetailsSchema.default(emptyServiceDetailsInput),
  categoryId: z.string().min(1).optional().nullable(),
  price: z
    .string()
    .trim()
    .regex(/^\d+([.,]\d{1,2})?$/)
    .refine((value) => Number(value.replace(',', '.')) >= 0),
  durationMinutes: z.number().int().min(15).max(1440),
  capacity: z.number().int().min(1).max(100).default(1),
  status: serviceStatusSchema.default('draft'),
  pricingMode: servicePricingModeSchema.default('fixed'),
  bookingMode: serviceBookingModeSchema.default('appointment'),
});

export const serviceBookingInputSchema = z.object({
  serviceId: z.string().min(1),
  customerName: z.string().trim().min(2).max(255),
  customerEmail: z.email().max(255),
  customerPhone: z.string().trim().max(50).optional().nullable(),
  scheduledStart: z.iso.datetime(),
  customerNotes: z.string().trim().max(2000).optional().nullable(),
});

export type ServiceInput = z.infer<typeof serviceInputSchema>;
export type ServiceBookingInput = z.infer<typeof serviceBookingInputSchema>;

export const serviceKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{0,63}$/);

export const materialUnitSchema = z.enum([
  'unit',
  'hour',
  'liter',
  'kilogram',
  'square_meter',
  'meter',
]);

const finiteNonNegativeNumberSchema = z.number().finite().min(0);
const nullableFiniteNonNegativeNumberSchema =
  finiteNonNegativeNumberSchema.nullable();
const serviceConfigurationValueSchema = z.union([
  z.number().finite(),
  z.string().max(2000),
  z.boolean(),
]);
const nullableServiceConfigurationValueSchema =
  serviceConfigurationValueSchema.nullable();

function isFieldValueTypeValid(
  type: 'number' | 'text' | 'select' | 'checkbox',
  value: z.infer<typeof serviceConfigurationValueSchema>,
): boolean {
  if (type === 'number') return typeof value === 'number';
  if (type === 'checkbox') return typeof value === 'boolean';
  return typeof value === 'string';
}

export const serviceFieldOptionSchema = z
  .object({
    value: z.string().min(1).max(255),
    label: z.string().min(1).max(255),
  })
  .strict();

export const materialInputSchema = z
  .object({
    categoryId: z.string().min(1).max(21).optional().nullable(),
    sku: z.string().trim().min(1).max(100),
    name: z.string().trim().min(1).max(255),
    manufacturer: z.string().trim().max(255).optional().nullable(),
    reference: z.string().trim().max(255).optional().nullable(),
    unit: materialUnitSchema,
    purchasePrice: nullableFiniteNonNegativeNumberSchema.optional(),
    salePrice: finiteNonNegativeNumberSchema,
    taxRate: z.number().finite().min(0).max(100).optional().nullable(),
    coveragePerUnit: z.number().finite().positive().optional().nullable(),
    isActive: z.boolean().default(true),
    displayOrder: z.number().int().min(0).max(1_000_000).default(0),
  })
  .strict();

export const serviceMaterialCategoryInputSchema = z
  .object({
    name: z.string().trim().min(1).max(255),
    description: z.string().trim().max(2000).optional().nullable(),
    displayOrder: z.number().int().min(0).max(1_000_000).default(0),
  })
  .strict();

export const serviceMaterialSnapshotSchema = z
  .object({
    id: z.string().min(1).max(21),
    sku: z.string().min(1).max(100),
    name: z.string().min(1).max(255),
    manufacturer: z.string().max(255).nullable(),
    reference: z.string().max(255).nullable(),
    unit: materialUnitSchema,
    salePrice: finiteNonNegativeNumberSchema,
    taxRate: z.number().finite().min(0).max(100).nullable(),
    coveragePerUnit: z.number().finite().positive().nullable(),
  })
  .strict();

export const serviceTemplateFieldSchema = z
  .object({
    key: serviceKeySchema,
    label: z.string().trim().min(1).max(255),
    description: z.string().trim().max(2000).nullable(),
    type: z.enum(['number', 'text', 'select', 'checkbox']),
    required: z.boolean(),
    defaultValue: nullableServiceConfigurationValueSchema,
    options: z.array(serviceFieldOptionSchema).max(100),
    visibilityCondition: z.string().trim().min(1).max(512).nullable(),
    position: z.number().int().min(0).max(1_000_000),
  })
  .strict()
  .superRefine((field, context) => {
    if (
      field.defaultValue !== null &&
      !isFieldValueTypeValid(field.type, field.defaultValue)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Default value must match the field type',
        path: ['defaultValue'],
      });
    }
    if (field.type === 'select' && field.options.length === 0) {
      context.addIssue({
        code: 'custom',
        message: 'Select fields require at least one option',
        path: ['options'],
      });
    }
    if (field.type !== 'select' && field.options.length > 0) {
      context.addIssue({
        code: 'custom',
        message: 'Only select fields can define options',
        path: ['options'],
      });
    }
    if (
      field.type === 'select' &&
      field.defaultValue !== null &&
      typeof field.defaultValue === 'string' &&
      !field.options.some((option) => option.value === field.defaultValue)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Default value must match a select option',
        path: ['defaultValue'],
      });
    }
    if (
      new Set(field.options.map((option) => option.value)).size !==
      field.options.length
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Option values must be unique',
        path: ['options'],
      });
    }
  });

export const serviceTemplateLineSchema = z
  .object({
    code: serviceKeySchema,
    description: z.string().trim().min(1).max(1000),
    unit: z.string().trim().min(1).max(50),
    quantityFormula: z.string().trim().min(1).max(512),
    priceFormula: z.string().trim().min(1).max(512).nullable(),
    unitPrice: nullableFiniteNonNegativeNumberSchema,
    materialId: z.string().min(1).max(21).nullable(),
    optional: z.boolean(),
    defaultEnabled: z.boolean(),
    conditionExpression: z.string().trim().min(1).max(512).nullable(),
    position: z.number().int().min(0).max(1_000_000),
  })
  .strict()
  .superRefine((line, context) => {
    if (
      line.priceFormula === null &&
      line.unitPrice === null &&
      line.materialId === null
    ) {
      context.addIssue({
        code: 'custom',
        message: 'A line requires a price formula, unit price, or material',
        path: ['priceFormula'],
      });
    }
  });

export const serviceTemplateSchema = z
  .object({
    fields: z.array(serviceTemplateFieldSchema).max(50),
    lines: z.array(serviceTemplateLineSchema).max(100),
  })
  .strict()
  .superRefine((template, context) => {
    const fieldKeys = new Set<string>();
    for (const [index, field] of template.fields.entries()) {
      if (fieldKeys.has(field.key)) {
        context.addIssue({
          code: 'custom',
          message: 'Field keys must be unique',
          path: ['fields', index, 'key'],
        });
      }
      fieldKeys.add(field.key);
    }

    const lineCodes = new Set<string>();
    for (const [index, line] of template.lines.entries()) {
      if (lineCodes.has(line.code)) {
        context.addIssue({
          code: 'custom',
          message: 'Line codes must be unique',
          path: ['lines', index, 'code'],
        });
      }
      lineCodes.add(line.code);
    }
  });

export const serviceConfigurationValuesSchema = z
  .record(serviceKeySchema, serviceConfigurationValueSchema)
  .superRefine((configuration, context) => {
    if (Object.keys(configuration).length > 50) {
      context.addIssue({
        code: 'custom',
        message: 'Configuration cannot contain more than 50 values',
      });
    }
  });

export const serviceSaveTemplateInputSchema = z
  .object({
    serviceId: z.string().min(1).max(21),
    fields: z.array(serviceTemplateFieldSchema).max(50),
    lines: z.array(serviceTemplateLineSchema).max(100),
  })
  .strict()
  .superRefine((input, context) => {
    const result = serviceTemplateSchema.safeParse({
      fields: input.fields,
      lines: input.lines,
    });
    if (!result.success) {
      for (const issue of result.error.issues) {
        context.addIssue({
          code: 'custom',
          message: issue.message,
          path: issue.path,
        });
      }
    }
  });

export const servicePreviewEvaluationInputSchema = z
  .object({
    serviceId: z.string().min(1).max(21),
    configuration: serviceConfigurationValuesSchema,
    enabledOptionalLineCodes: z.array(serviceKeySchema).max(100).optional(),
  })
  .strict();

export const publicServiceEvaluationInputSchema = z
  .object({
    id: z.string().min(1).max(21),
    configuration: serviceConfigurationValuesSchema,
    enabledOptionalLineCodes: z.array(serviceKeySchema).max(100).optional(),
  })
  .strict();

export const publicServiceRequestQuoteInputSchema = z
  .object({
    id: z.string().min(1).max(21),
    name: z.string().trim().min(2).max(255),
    email: z.email().max(255),
    phone: z.string().trim().max(50).optional().nullable(),
    notes: z.string().trim().max(2000).optional().nullable(),
    configuration: serviceConfigurationValuesSchema,
    enabledOptionalLineCodes: z.array(serviceKeySchema).max(100).optional(),
  })
  .strict();

export const serviceEstimateLineOutputSchema = z.object({
  code: z.string(),
  description: z.string(),
  unit: z.string(),
  quantity: z.number(),
  unitPrice: z.number(),
  subtotal: z.number(),
  taxRate: z.number(),
  taxAmount: z.number(),
  total: z.number(),
  material: serviceMaterialSnapshotSchema.nullable(),
});

export const serviceEvaluationOutputSchema = z.object({
  configuration: serviceConfigurationValuesSchema,
  lines: z.array(serviceEstimateLineOutputSchema),
  subtotal: z.number(),
  taxAmount: z.number(),
  total: z.number(),
});

export const evaluateServiceTemplateInputSchema = z
  .object({
    template: serviceTemplateSchema,
    configuration: serviceConfigurationValuesSchema,
    materials: z.array(serviceMaterialSnapshotSchema).max(100),
    enabledOptionalLineCodes: z.array(serviceKeySchema).max(100).optional(),
    defaultTaxRate: z.number().finite().min(0).max(100).optional(),
  })
  .strict();

export type MaterialInput = z.infer<typeof materialInputSchema>;
export type ServiceMaterialCategoryInput = z.infer<
  typeof serviceMaterialCategoryInputSchema
>;
export type ServiceTemplateInput = z.infer<typeof serviceTemplateSchema>;
export type ServiceConfigurationValues = z.infer<
  typeof serviceConfigurationValuesSchema
>;
export type EvaluateServiceTemplateInput = z.infer<
  typeof evaluateServiceTemplateInputSchema
>;
export type ServiceSaveTemplateInput = z.infer<
  typeof serviceSaveTemplateInputSchema
>;
export type ServicePreviewEvaluationInput = z.infer<
  typeof servicePreviewEvaluationInputSchema
>;
export type PublicServiceRequestQuoteInput = z.infer<
  typeof publicServiceRequestQuoteInputSchema
>;
